import React from 'react';
import { graphql, useFragment } from 'react-relay';
import Presence from '@atlaskit/avatar/presence';
import type { ActivityStatusIndicatorPresence_agent$key } from './__generated__/ActivityStatusIndicatorPresence_agent.graphql.ts';
import { getGlobalPresenceStatus } from './getGlobalPresenceStatus.tsx';
import { MixedStatusIcon } from './MixedStatusIcon.tsx';

type Props = {
	// Fallback to graphql derived available count if source of truth Twilio
	// worker attributes are not available
	availableSpacesCount?: number;
	agentFragment: ActivityStatusIndicatorPresence_agent$key;
	workerActivity?: string;
};

export const ActivityStatusIndicatorPresence = ({
	availableSpacesCount,
	agentFragment,
	workerActivity,
}: Props): React.JSX.Element => {
	const agentData = useFragment(
		graphql`
			fragment ActivityStatusIndicatorPresence_agent on CustomerServiceLiveChatAgent {
				liveChatActivitiesBySpace {
					edges {
						node {
							activityStatus
						}
					}
				}
			}
		`,
		agentFragment,
	);

	// If the worker activity is Offline, all spaces are offline
	if (workerActivity?.toLowerCase() === 'offline') {
		return <Presence presence="offline" />;
	}

	const edges = agentData.liveChatActivitiesBySpace?.edges ?? [];
	const graphqlAvailableCount = edges.filter(
		(edge) => edge?.node?.activityStatus === 'available',
	).length;

	const status = getGlobalPresenceStatus(
		availableSpacesCount ?? graphqlAvailableCount,
		edges.length,
	);

	if (status === 'offline') return <Presence presence="offline" />;
	if (status === 'mixed') return <Presence>{MixedStatusIcon}</Presence>;
	return <Presence presence="online" />;
};
